// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useOrcamento.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orcamentoService } from '../services/orcamentoService';
import type { ListaFiltrosPessoa } from '../services/davService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarOrcamentoRequest, AtualizarOrcamentoRequest } from '../types';

interface UseOrcamentoParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, cliente, vendedor e ordem — aplicados no servidor. */
  filtros?: ListaFiltrosPessoa;
}

export const useOrcamento = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UseOrcamentoParams = {}) => {
  const queryClient = useQueryClient();

  // A lista já traz `resumo` (KPIs do filtro sem o status): uma ida ao servidor.
  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['orcamento', pagina, tamanhoPagina, filtros],
    queryFn: () => orcamentoService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  const orcamentos   = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumo       = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (request: CriarOrcamentoRequest) => orcamentoService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      toast.success('Orçamento criado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarOrcamentoRequest }) =>
      orcamentoService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      toast.success('Orçamento atualizado com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => orcamentoService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      toast.success('Orçamento excluído com sucesso');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const converterMutation = useMutation({
    mutationFn: (id: number) => orcamentoService.converterEmDav(id),
    onSuccess: (dav) => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      toast.success(`Orçamento convertido — DAV nº ${dav.numeroDav} criado`);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    orcamentos,
    total,
    totalPaginas,
    resumo,
    isLoading,
    isFetching,
    createOrcamento: createMutation.mutate,
    updateOrcamento: updateMutation.mutate,
    deleteOrcamento: deleteMutation.mutate,
    converterEmDav:  converterMutation.mutateAsync,
    isCreating:      createMutation.isPending,
    isConvertendo:   converterMutation.isPending,
  };
};
